/**
 * storage-page.js — Sanity integration for the Storage V4 page layout
 *
 * Responsibilities:
 *  - Fetch the technologyPage doc with slug "storage" from Sanity
 *  - Show/hide sections via show* boolean flags
 *  - Inject content into data-sv4 elements and re-render dynamic sections
 *  - Add Sanity edit-mode buttons (getSanityEditLink) in each section
 *  - Manage navbar scroll transparency
 */

import { client, urlFor, getSanityEditLink, resolveInternalLink } from './sanity-client';

// ── GROQ query ─────────────────────────────────────────────────────────────────
const QUERY = `*[_type == "technologyPage" && slug.current == "storage"][0]{
  _id, title, slug,
  showHero, showProductDev, showPerformance, showCaseStudies, showCta,
  heroDescription, heroPractice, heroSurfaceArea, heroPlatforms,
  pdLeadTitle, pdLeadLede,
  pdLeadStats[]{ label, body },
  sections[]{ _key, title, description, tags },
  performanceSection{
    heading, lede, pullQuote, signature,
    cards[]{
      _key, indexLabel, heading, body, cardType,
      meters[]{ _key, label, value, percent },
      stackList[]{ _key, key, value }
    }
  },
  "caseStudies": caseStudies[@->visibility != false]->{ _id, title, "slug": slug.current, image, description },
  ctaHeadline, ctaLede, ctaLocations
}`;

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Parse *asterisk* spans into <em class="sv4-accent"> */
function parseAccent(text) {
  if (!text) return '';
  return text.replace(/\*([^*]+)\*/g, '<em style="font-style:normal;color:var(--sv4-accent);font-weight:700">$1</em>');
}

/** Set text/innerHTML on a data-sv4 element, guarding against missing nodes */
function setField(attr, value, html = false) {
  const el = document.querySelector(`[data-sv4="${attr}"]`);
  if (!el || !value) return;
  if (html) el.innerHTML = value;
  else el.textContent = value;
}

/** Show or hide a section by ID */
function setVisible(id, visible) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.display = visible === false ? 'none' : '';
}

// ── Section renderers ──────────────────────────────────────────────────────────

function renderHero(data, docId) {
  // Title (keep the red dot span intact)
  const titleEl = document.querySelector('[data-sv4="title"]');
  if (titleEl && data.title) {
    titleEl.innerHTML = `${data.title}<span class="dot">.</span>`;
  }

  setField('heroDescription', data.heroDescription);
  setField('heroPractice',    data.heroPractice);
  setField('heroSurfaceArea', data.heroSurfaceArea);
  setField('heroPlatforms',   data.heroPlatforms);

  // Edit button
  const heroSection = document.getElementById('sv4-hero');
  if (heroSection && docId) {
    heroSection.classList.add('position-relative');
    heroSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'technologyPage', 'heroDescription', 'Edit Hero'));
  }
}

function renderProductDevLead(data, docId) {
  // Lead headline — supports *accent* syntax
  const leadTitleEl = document.querySelector('[data-sv4="pdLeadTitle"]');
  if (leadTitleEl && data.pdLeadTitle) {
    leadTitleEl.innerHTML = parseAccent(data.pdLeadTitle);
  }

  setField('pdLeadLede', data.pdLeadLede);

  // Stats grid
  const statsEl = document.getElementById('sv4-pd-stats');
  if (statsEl && data.pdLeadStats && data.pdLeadStats.length) {
    statsEl.innerHTML = data.pdLeadStats.map(s =>
      `<span class="num">${s.label || ''}</span><span class="body">${s.body || ''}</span>`
    ).join('');
    statsEl.style.display = '';
  }

  // Edit button on the lead column
  const leadEl = document.querySelector('.sv4-pd-lead');
  if (leadEl && docId) {
    leadEl.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'technologyPage', 'pdLeadTitle', 'Edit Product Dev Lead'));
  }
}

function renderProductDevItems(sections, docId) {
  const container = document.getElementById('sv4-pd-items');
  if (!container || !sections || !sections.length) return;

  container.innerHTML = sections.map((sec, i) => {
    const idx = String(i + 1).padStart(2, '0');
    const tagsHtml = (sec.tags && sec.tags.length)
      ? `<div class="tags">${sec.tags.map(t => `<span class="tag">${t}</span>`).join('')}</div>`
      : '';
    const editBtn = docId
      ? getSanityEditLink(docId, 'technologyPage',
          sec._key ? `sections[_key=="${sec._key}"]` : `sections[${i}]`,
          `Edit Item ${idx}`)
      : '';

    return `
      <article class="sv4-pd-item" data-reveal style="--rd:${i * 80}ms">
        <span class="marker"></span>
        <span class="num">${idx}</span>
        <div style="position:relative">
          ${editBtn}
          <h3>${sec.title || ''}</h3>
          <p>${sec.description || ''}</p>
          ${tagsHtml}
        </div>
      </article>`;
  }).join('');

  // Re-attach intersection observer for reveal + marker dots
  attachItemObserver(container.querySelectorAll('.sv4-pd-item'));
}

function renderPerformance(perf, docId) {
  if (!perf) return;

  // Heading and lede
  const headEl = document.querySelector('[data-sv4="perfHeading"]');
  if (headEl && perf.heading) headEl.innerHTML = parseAccent(perf.heading);

  setField('perfLede', perf.lede);

  // Pull quote
  const pullEl = document.querySelector('[data-sv4="pullQuote"]');
  if (pullEl && perf.pullQuote) pullEl.innerHTML = parseAccent(perf.pullQuote);

  if (perf.signature) setField('perfSignature', perf.signature);

  // Cards
  const grid = document.getElementById('sv4-pq-grid');
  if (grid && perf.cards && perf.cards.length) {
    grid.innerHTML = perf.cards.map((card, i) => {
      const ghostNum = String(i + 1).padStart(2, '0');
      let contentHtml = '';

      if (card.cardType === 'stacklist' && card.stackList && card.stackList.length) {
        contentHtml = `<ul class="stack-list">
          ${card.stackList.map(item =>
            `<li><span class="k">${item.key || ''}</span><span class="v">${item.value || ''}</span></li>`
          ).join('')}
        </ul>`;
      } else if (card.meters && card.meters.length) {
        contentHtml = `<div class="meter">
          ${card.meters.map(m =>
            `<div class="meter-row">
               <span class="lbl">${m.label || ''}</span>
               <span class="track"><span class="fill" style="--w:${m.percent || 0}%"></span></span>
               <span class="val">${m.value || ''}</span>
             </div>`
          ).join('')}
        </div>`;
      }

      const editBtn = docId
        ? getSanityEditLink(docId, 'technologyPage',
            card._key
              ? `performanceSection.cards[_key=="${card._key}"]`
              : `performanceSection.cards[${i}]`,
            `Edit Card: ${card.indexLabel || i + 1}`)
        : '';

      return `
        <article class="sv4-pq-card" data-reveal style="--rd:${i * 80}ms">
          <span class="ghost-num" aria-hidden="true">${ghostNum}</span>
          ${editBtn ? `<div style="position:absolute;top:1rem;right:1rem;z-index:10">${editBtn}</div>` : ''}
          <span class="index">${card.indexLabel || ''}</span>
          <h3>${card.heading || ''}</h3>
          <p>${card.body || ''}</p>
          ${contentHtml}
        </article>`;
    }).join('');
  }

  // Edit button on the "How we measure" head block (eyebrow + heading + lede)
  const perfHead = document.querySelector('#sv4-performance .sv4-pq-head');
  if (perfHead && docId) {
    perfHead.style.position = 'relative';
    perfHead.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'technologyPage', 'performanceSection', 'Edit How We Measure'));
  }

  // Re-attach meter observer
  attachCardObserver(document.querySelectorAll('#sv4-pq-grid .sv4-pq-card'));
}

function renderCaseStudies(caseStudies, docId) {
  const grid = document.getElementById('sv4-cs-grid');
  if (!grid || !caseStudies || !caseStudies.length) return;

  // SVG glyphs (alternating designs)
  const glyphs = [
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <path d="M100 14 L172 44 V104 C172 144 140 174 100 188 C60 174 28 144 28 104 V44 Z"/>
       <rect x="74" y="92" width="52" height="44" rx="3"/>
       <path d="M84 92 V78 a16 16 0 0 1 32 0 V92"/>
       <circle cx="100" cy="114" r="5" fill="currentColor"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <circle cx="64" cy="100" r="26"/>
       <path d="M86 100 H170"/>
       <path d="M150 100 V120"/>
       <path d="M132 100 V116"/>
       <path d="M170 100 V124"/>
       <circle cx="64" cy="100" r="6" fill="currentColor"/>
     </svg>`,
  ];

  const artBgs = [
    '', // card 0 uses default CSS art background
    'radial-gradient(120% 80% at 10% 90%, rgba(229,50,45,.25), transparent 55%), radial-gradient(60% 60% at 90% 10%, rgba(120,180,255,.18), transparent 60%), linear-gradient(135deg, #0F1114 0%, #14181E 60%, #0A0B0D 100%)',
  ];

  grid.innerHTML = caseStudies.map((cs, i) => {
    const href = cs.title
      ? resolveInternalLink(`/case-studies/case-study.html?title=${encodeURIComponent(cs.title)}`)
      : '#';

    const hasImage = cs.image && cs.image.asset;
    const imgUrl = hasImage ? urlFor(cs.image).width(800).quality(80).url() : '';
    const artStyle = hasImage
      ? `style="background-image:linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.65)), url('${imgUrl}');background-size:cover;background-position:center"`
      : (artBgs[i] ? `style="background:${artBgs[i]}"` : '');
    const glyph = glyphs[i % glyphs.length];

    return `
      <a href="${href}" class="sv4-cs-card" data-reveal style="--rd:${i * 120}ms">
        <div class="art" ${artStyle}>
          <div class="art-grid"></div>
          ${hasImage ? '' : `<div class="art-glyph">${glyph}</div>`}
        </div>
        <div class="read" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14M13 5l7 7-7 7"/>
          </svg>
        </div>
        <span class="ribbon"></span>
        <div class="meta">
          <span class="cs-tag">Storage</span>
          <h3>${cs.title || ''}</h3>
        </div>
      </a>`;
  }).join('');

  // Edit button on section
  const csSection = document.getElementById('sv4-case-studies');
  if (csSection && docId) {
    csSection.classList.add('position-relative');
    csSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'technologyPage', 'caseStudies', 'Edit Case Studies'));
  }

  // Re-attach reveal observer
  attachRevealObserver(grid.querySelectorAll('[data-reveal]'));
}

function renderCta(data, docId) {
  if (data.ctaHeadline) {
    const el = document.querySelector('[data-sv4="ctaHeadline"]');
    if (el) el.innerHTML = parseAccent(data.ctaHeadline);
  }
  setField('ctaLede',      data.ctaLede);
  setField('ctaLocations', data.ctaLocations);

  const ctaSection = document.getElementById('sv4-cta');
  if (ctaSection && docId) {
    ctaSection.classList.add('position-relative');
    ctaSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'technologyPage', 'ctaHeadline', 'Edit CTA'));
  }
}

// ── IntersectionObserver helpers ──────────────────────────────────────────────

function attachRevealObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  els.forEach(el => io.observe(el));
}

function attachItemObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.3 });
  els.forEach(el => io.observe(el));
  // also trigger reveal
  attachRevealObserver(Array.from(els).filter(el => el.hasAttribute('data-reveal')));
}

function attachCardObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.4 });
  els.forEach(el => io.observe(el));
}

// ── Navbar transparency ───────────────────────────────────────────────────────

function initNavbarScroll() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;
  function onScroll() {
    if (window.scrollY > 50) {
      navbar.classList.add('navbar-v3-scrolled');
      navbar.classList.remove('navbar-v3-transparent');
    } else {
      navbar.classList.remove('navbar-v3-scrolled');
      navbar.classList.add('navbar-v3-transparent');
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── Bootstrap reveal on all static [data-reveal] elements ────────────────────

function initStaticReveal() {
  const els = document.querySelectorAll('.sv4-wrap [data-reveal]');
  attachRevealObserver(els);
  // Fallback: force-show after 1.2 s
  setTimeout(() => {
    document.querySelectorAll('.sv4-wrap [data-reveal]:not(.in)').forEach(el => el.classList.add('in'));
  }, 1200);
}

function initStaticCardObserver() {
  attachCardObserver(document.querySelectorAll('.sv4-wrap .sv4-pq-card'));
  attachItemObserver(document.querySelectorAll('.sv4-wrap .sv4-pd-item'));
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  initNavbarScroll();
  initStaticReveal();
  initStaticCardObserver();

  let data;
  try {
    data = await client.fetch(QUERY);
  } catch (err) {
    console.warn('[storage-page] Sanity fetch failed — using static HTML fallback.', err);
    return;
  }

  if (!data) {
    console.warn('[storage-page] No Sanity document found for slug "storage".');
    return;
  }

  const docId = data._id;

  // ── Visibility ──
  setVisible('sv4-hero',         data.showHero);
  setVisible('sv4-product-dev',  data.showProductDev);
  setVisible('sv4-performance',  data.showPerformance);
  setVisible('sv4-case-studies', data.showCaseStudies);
  setVisible('sv4-cta',          data.showCta);

  // ── Content injection ──
  if (data.showHero !== false)        renderHero(data, docId);
  if (data.showProductDev !== false)  {
    renderProductDevLead(data, docId);
    if (data.sections && data.sections.length) renderProductDevItems(data.sections, docId);
  }
  if (data.showPerformance !== false && data.performanceSection) {
    renderPerformance(data.performanceSection, docId);
  }
  if (data.showCaseStudies !== false && data.caseStudies && data.caseStudies.length) {
    renderCaseStudies(data.caseStudies, docId);
  }
  if (data.showCta !== false) renderCta(data, docId);
});
